/**
 * Authentication Service
 *
 * Handles Learnworlds authentication flow including:
 * - Session token validation with backend
 * - Token storage and retrieval
 * - Session management
 * - Authentication state lifecycle
 *
 * [A007] Implementation
 */

import { apiService } from './apiService'
import type { LearnworldsAuthParams } from '../utils/urlParams'

/**
 * Session token information
 */
export interface SessionToken {
  token: string
  expiresAt: Date
}

/**
 * Authenticated user information
 */
export interface AuthenticatedUser {
  id: string
  email: string
  name?: string
}

/**
 * Authentication response from backend
 */
export interface AuthResponse {
  success: boolean
  sessionToken: string
  user: {
    id: string
    email: string
    name?: string
  }
  expiresAt: string
  meta?: {
    timestamp: string
  }
}

/**
 * Token validation response from backend
 */
export interface TokenValidationResponse {
  success: boolean
  valid: boolean
  user?: {
    id: string
    email: string
    name?: string
  }
  expiresAt?: string
  error?: string
}

/**
 * Authentication error class
 */
export class AuthenticationError extends Error {
  public readonly code: string
  public readonly details?: string[]

  constructor(message: string, code: string, details?: string[]) {
    super(message)
    this.name = 'AuthenticationError'
    this.code = code
    this.details = details
  }
}

/**
 * Storage keys for session data
 */
const STORAGE_KEYS = {
  SESSION_TOKEN: 'lw_session_token',
  SESSION_EXPIRES: 'lw_session_expires',
  USER_DATA: 'lw_user_data',
} as const

/**
 * Authentication Service class
 */
export class AuthService {
  private sessionToken: string | null = null
  private user: AuthenticatedUser | null = null

  /**
   * Validate authentication with backend and create session
   *
   * @param params - Learnworlds authentication parameters from URL
   * @returns Session token and user information
   * @throws AuthenticationError if validation fails
   *
   * @example
   * const params = extractAuthParams()
   * const { token, user } = await authService.validateAuth(params)
   * console.log('Authenticated:', user.email)
   */
  async validateAuth(params: LearnworldsAuthParams): Promise<{
    token: string
    user: AuthenticatedUser
    expiresAt: Date
  }> {
    try {
      // Call backend validation endpoint
      const response = await apiService.post<AuthResponse>('/games/validate-auth', {
        userId: params.userId,
        email: params.email,
        name: params.name,
      })

      if (!response.success) {
        throw new AuthenticationError(
          'Authentication validation failed',
          'VALIDATION_FAILED'
        )
      }

      // Store session data
      const expiresAt = new Date(response.expiresAt)
      this.sessionToken = response.sessionToken
      this.user = {
        id: response.user.id,
        email: response.user.email,
        name: response.user.name,
      }

      // Persist to storage
      this.saveToStorage(response.sessionToken, expiresAt, this.user)

      // Set token in API service for automatic injection
      apiService.setAuthToken(response.sessionToken)

      console.log('[Auth] Authentication successful:', {
        userId: this.user.id,
        email: this.user.email,
      })

      return {
        token: response.sessionToken,
        user: this.user,
        expiresAt,
      }
    } catch (error) {
      // Handle API errors
      if (error instanceof Error && 'status' in error) {
        const apiError = error as any
        throw new AuthenticationError(
          apiError.message || 'Authentication failed',
          `HTTP_${apiError.status}`,
          apiError.data?.details
        )
      }

      // Re-throw AuthenticationError
      if (error instanceof AuthenticationError) {
        throw error
      }

      // Unknown error
      throw new AuthenticationError(
        error instanceof Error ? error.message : 'Unknown authentication error',
        'UNKNOWN_ERROR'
      )
    }
  }

  /**
   * Validate an existing session token with backend
   *
   * @param token - Session token to validate
   * @returns true if token is valid
   */
  async validateToken(token: string): Promise<boolean> {
    try {
      const response = await apiService.post<TokenValidationResponse>(
        '/games/validate-token',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.valid && response.user) {
        // Update stored user data
        this.user = {
          id: response.user.id,
          email: response.user.email,
          name: response.user.name,
        }

        if (response.expiresAt) {
          const expiresAt = new Date(response.expiresAt)
          this.saveToStorage(token, expiresAt, this.user)
        }

        return true
      }

      return false
    } catch (error) {
      console.error('[Auth] Token validation failed:', error)
      return false
    }
  }

  /**
   * Restore session from local storage
   *
   * @returns true if session was successfully restored
   *
   * @example
   * if (authService.restoreSession()) {
   *   console.log('Session restored')
   * } else {
   *   console.log('No valid session found')
   * }
   */
  async restoreSession(): Promise<boolean> {
    try {
      // Load from storage
      const token = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN)
      const expiresStr = localStorage.getItem(STORAGE_KEYS.SESSION_EXPIRES)
      const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA)

      if (!token || !expiresStr || !userStr) {
        return false
      }

      // Check expiration
      const expiresAt = new Date(expiresStr)
      if (new Date() >= expiresAt) {
        console.log('[Auth] Stored session expired')
        this.clearSession()
        return false
      }

      // Validate token with backend
      const valid = await this.validateToken(token)
      if (!valid) {
        console.log('[Auth] Stored session invalid')
        this.clearSession()
        return false
      }

      // Restore session
      this.sessionToken = token
      this.user = JSON.parse(userStr)
      apiService.setAuthToken(token)

      console.log('[Auth] Session restored:', {
        userId: this.user?.id,
        email: this.user?.email,
      })

      return true
    } catch (error) {
      console.error('[Auth] Failed to restore session:', error)
      this.clearSession()
      return false
    }
  }

  /**
   * Revoke current session (logout)
   *
   * @example
   * await authService.logout()
   * console.log('Logged out successfully')
   */
  async logout(): Promise<void> {
    try {
      // Attempt to revoke token on backend
      if (this.sessionToken) {
        await apiService.post('/games/revoke-token', {})
      }
    } catch (error) {
      console.error('[Auth] Failed to revoke token on backend:', error)
      // Continue with local logout even if backend fails
    } finally {
      // Always clear local session
      this.clearSession()
    }
  }

  /**
   * Clear local session data
   */
  clearSession(): void {
    this.sessionToken = null
    this.user = null
    apiService.setAuthToken(null)

    // Clear from storage
    localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRES)
    localStorage.removeItem(STORAGE_KEYS.USER_DATA)

    console.log('[Auth] Session cleared')
  }

  /**
   * Get current session token
   *
   * @returns Session token if authenticated, null otherwise
   */
  getSessionToken(): string | null {
    return this.sessionToken
  }

  /**
   * Get current authenticated user
   *
   * @returns User information if authenticated, null otherwise
   */
  getUser(): AuthenticatedUser | null {
    return this.user
  }

  /**
   * Check if user is currently authenticated
   *
   * @returns true if user has valid session
   */
  isAuthenticated(): boolean {
    return Boolean(this.sessionToken && this.user)
  }

  /**
   * Save session data to local storage
   */
  private saveToStorage(token: string, expiresAt: Date, user: AuthenticatedUser): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token)
      localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRES, expiresAt.toISOString())
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user))
    } catch (error) {
      console.error('[Auth] Failed to save to storage:', error)
      // Continue without storage (session will be memory-only)
    }
  }
}

/**
 * Default authentication service instance
 */
export const authService = new AuthService()

/**
 * Create a new authentication service instance
 */
export function createAuthService(): AuthService {
  return new AuthService()
}
